import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Card } from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sel, Field } from "../components/Sel";
import { RoleBadge } from "../components/Badges";

const EMPTY = { name: "", email: "", password: "", role: "sales", department: "Sales", designation: "", phone: "", active: true };
const DEPT = { admin: "Management", sales: "Sales", hr: "HR", accounts: "Accounts" };

const EmployeeDialog = ({ open, onOpenChange, emp, onSaved }) => {
  const { user } = useAuth();
  const [f, setF] = useState(EMPTY);
  useEffect(() => { if (open) setF(emp ? { ...EMPTY, ...emp, password: "" } : EMPTY); }, [open, emp]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const roles = user.role === "admin" ? ["admin", "sales", "hr", "accounts"] : ["sales", "hr", "accounts"];

  const save = async () => {
    try {
      const body = { ...f, password: f.password || null };
      emp ? await api.put(`/users/${emp.id}`, body) : await api.post("/users", body);
      toast.success(emp ? "Employee updated" : "Employee created");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{emp ? "Edit employee" : "Add employee"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name"><Input data-testid="emp-name-input" value={f.name} onChange={set("name")} /></Field>
          <Field label="Email"><Input data-testid="emp-email-input" type="email" value={f.email} onChange={set("email")} /></Field>
          <Field label={emp ? "New password (optional)" : "Password"}><Input data-testid="emp-password-input" type="password" value={f.password} onChange={set("password")} /></Field>
          <Field label="Phone"><Input data-testid="emp-phone-input" value={f.phone} onChange={set("phone")} /></Field>
          <Field label="Role"><Sel testid="emp-role-select" value={f.role} onChange={(r) => setF((p) => ({ ...p, role: r, department: DEPT[r] }))} options={roles.map((r) => ({ value: r, label: r === "hr" ? "HR" : r }))} /></Field>
          <Field label="Designation"><Input data-testid="emp-designation-input" value={f.designation} onChange={set("designation")} /></Field>
          <div className="flex items-center gap-3"><Switch data-testid="emp-active-switch" checked={f.active} onCheckedChange={set("active")} /><span className="text-sm">Active account</span></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={save} data-testid="emp-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Employees() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [open, setOpen] = useState(false);
  const [emp, setEmp] = useState(null);
  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rows = list.filter((u) => (role === "all" || u.role === role) && `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="employees-page">
      <PageHeader title="Employees" subtitle="Manage team members and their CRM access.">
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setEmp(null); setOpen(true); }} data-testid="add-employee-btn"><Plus className="h-4 w-4 mr-1" />Add employee</Button>
      </PageHeader>
      <Card className="p-4 mb-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input data-testid="employee-search-input" className="pl-9" placeholder="Search employees..." value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <Sel testid="employee-role-filter" className="sm:w-44" value={role} onChange={setRole} options={[{ value: "all", label: "All roles" }, "admin", "sales", "hr", "accounts"].map((r) => (typeof r === "string" ? { value: r, label: r === "hr" ? "HR" : r } : r))} />
      </Card>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead><tr className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-600">
            {["Employee", "Role", "Designation", "Phone", "Leads / Won", "Status", "Last login", ""].map((h) => <th key={h} className="px-4 py-3 whitespace-nowrap">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/80" data-testid={`employee-row-${u.id}`}>
                <td className="px-4 py-3"><div className="flex items-center gap-3"><div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-emerald-700 font-bold">{u.name[0]}</div><div><div className="font-semibold">{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div></div></td>
                <td className="px-4 py-3"><RoleBadge role={u.role} /></td>
                <td className="px-4 py-3 text-slate-600">{u.designation || "-"}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.phone || "-"}</td>
                <td className="px-4 py-3 font-mono">{u.lead_count ?? 0} / {u.won_count ?? 0}</td>
                <td className="px-4 py-3"><span className={`text-xs font-semibold ${u.active !== false ? "text-emerald-600" : "text-slate-400"}`}>{u.active !== false ? "Active" : "Inactive"}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(u.last_login)}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button className="p-1.5 text-slate-500 hover:text-emerald-600" onClick={() => { setEmp(u); setOpen(true); }} data-testid={`edit-employee-${u.id}`}><Pencil className="h-4 w-4" /></button>
                  {user.role === "admin" && u.id !== user.id && <button className="p-1.5 text-slate-500 hover:text-rose-600" onClick={() => del(u)} data-testid={`delete-employee-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <EmployeeDialog open={open} onOpenChange={setOpen} emp={emp} onSaved={load} />
    </div>
  );
}
